import React, { useEffect, useState, useMemo, useCallback } from 'react';
import styles from './DPViews.module.css';
import { styled } from "@mui/system";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import vector from "../../../../../assets/images/Vector.svg";
import CustomTooltip from '../../../../../utils/CustomTooltip';
import { smartProdSchedularInputFields } from 'Samarth Module/constants/dailyPhasing';
import { useSimpleInfiniteScroll } from 'Samarth Module/pages/dailyPhasing/hooks/useSimpleInfiniteScroll';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 600,
        borderTop: "1px solid #EAECF0",
        // borderRight: "1px solid #EAECF0",
        borderBottom: "1px solid #EAECF0",
        borderLeft: "1px solid #EAECF0",
        whiteSpace: "nowrap",
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: "12px",
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        borderTop: "1px solid #EAECF0",
        // borderRight: "1px solid #EAECF0",
        borderBottom: "1px solid #EAECF0",
        // borderLeft: "1px solid #EAECF0",
        textAlign: "center",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)({
    backgroundColor: "#FFFFFF !important"
});

function DPTableView({ tableData, columns, view }) {
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const [loading, setLoading] = useState(true);

    const selectedColumns = columns.filter((a) => a.visibility === true);

    // Set loading to false once we have data
    useEffect(() => {
        if (tableData && tableData.length >= 0) {
            setLoading(false);
        }
    }, [tableData]);

    // Sort the data directly without local state
    const sortedData = useMemo(() => {
        if (!tableData || tableData.length === 0) return [];
        
        let data = [...tableData];
        
        if (sortConfig.key) {
            data.sort((a, b) => {
                let valueA = a[sortConfig.key];
                let valueB = b[sortConfig.key];
                
                // Handle object values (date columns with Plan/Act/DFOS)
                if (valueA !== null && typeof valueA === "object") {
                    valueA = valueA["Plan"] || 0;
                }
                if (valueB !== null && typeof valueB === "object") {
                    valueB = valueB["Plan"] || 0;
                }
                
                // Try numeric comparison
                const numA = Number(valueA);
                const numB = Number(valueB);
                if (!isNaN(numA) && !isNaN(numB)) {
                    return sortConfig.direction === "asc" ? numA - numB : numB - numA;
                }
                
                // String comparison
                const strA = String(valueA || "");
                const strB = String(valueB || "");
                return sortConfig.direction === "asc"
                    ? strA.localeCompare(strB)
                    : strB.localeCompare(strA);
            });
        }
        
        return data;
    }, [tableData, sortConfig]);

    // Use simple infinite scroll hook (dedicated for this component)
    const { data: displayData, containerRef } = useSimpleInfiniteScroll(sortedData, 30);

    const rowTotal = (row, subCol) => {
        let total = 0;
        Object.keys(row).forEach((item) => {
            if (typeof row[item] === "object") {
                if (!row?.[item]?.[subCol] || typeof row?.[item]?.[subCol] !== "number") {
                    total += 0;
                } else {
                    total += row[item][subCol];
                }
            }
        });

        return total.toFixed(1);
    };

    const checkingTitle = (data) => {
        if (data && data === 'PM') {
            return 'Planned Maintainance'
        } else if (data && data === 'PH') {
            return 'Planned Holiday'
        }
    }

    // for calculate the colspan of plan, act row
    const subHeaderColSpan = () => {
        let count = 0;
        for (let col of selectedColumns) {
            if (!(col.label.includes(' - ') || smartProdSchedularInputFields.includes(col.label)) && col.visibility) {
                count++;
            }
        }
        return count;
    }

    const countSubColumns = () =>{
        let count = 0;
        for (let col of selectedColumns) {
            if ((col.label.includes(' - ') || smartProdSchedularInputFields.includes(col.label)) && col.visibility) {
                count++;
            }
        }
        return count + 1;
    }

    // Function to calculate the total for each column
    const calculateColumnTotal = (subName, colIdx) => {
        if (!sortedData || sortedData.length === 0) return '0.0';
        // Use selectedColumns order to match visual column order (instead of Object.keys which may differ)
        const dateKeys = selectedColumns
            .filter((col) => col.label.includes(' - ') || smartProdSchedularInputFields.includes(col.label))
            .map((col) => col.label);
        let count = 0;
        if(colIdx <= 6){
            for (let item of sortedData) {
                const value = item[dateKeys?.[colIdx]]?.[subName];
                if (value !== undefined && value !== null && value !== 'nan') {
                    const numValue = Number(value);
                    if (!isNaN(numValue)) {
                        count += numValue;
                    }
                }
            }
            return count.toFixed(1);
        } else {
            const planActData = {"Plan" : [], "Act" : [], "DFOS" : []};
            for (let item of sortedData) {
                planActData?.["Plan"].push(rowTotal(item, "Plan"));
                planActData?.["Act"].push(rowTotal(item, "Act"));
                if (view === 'DFOS') {
                    planActData?.["DFOS"].push(rowTotal(item, "DFOS"))
                }
            }
            let count = planActData[subName].reduce((acc, curr)=> {
                acc += parseFloat(curr);
                return acc;
            } ,0)
            return count.toFixed(1);
        }
    };

    const calculateMainColumnTotal = (colName) => { 
        if (!sortedData || sortedData.length === 0) return '0.0';
        let total = 0;
        for (let item of sortedData) {
            total += Number(item[colName]) ? Number(item[colName]) : 0;
        }
        return total.toFixed(1);
    }

    // Function to sorting the data
    const requestSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    }, []);

    return (
        loading ?
            <div>Data is loading...</div> :
            <>
                <TableContainer className={styles.table} sx={{ height: "71vh", overflow: "auto" }} ref={containerRef}>
                    <Table size="small">
                        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 999 }}>
                            {selectedColumns.length > 0 ? (
                                <>
                                    <TableRow sx={{ backgroundColor: "var(--primany-table-header-color)" }}>
                                        {selectedColumns.map((col, index) => {
                                            // Render Total column right after SAP Weekly Plan
                                            const isLastNonDateColumn = !col.label.includes(' - ') && !smartProdSchedularInputFields.includes(col.label) && 
                                                index === selectedColumns.findIndex((c, i) => i > index && (c.label.includes(' - ') || smartProdSchedularInputFields.includes(c.label))) - 1;
                                            
                                            return (
                                                <React.Fragment key={Math.random() * index}>
                                                    <StyledTableCell
                                                        colSpan={col.label.includes(' - ') || smartProdSchedularInputFields.includes(col.label) ? view === 'DFOS' ? 3 : 2 : 1}
                                                        onClick={() => col.sortable && requestSort(col.label)}
                                                        sx={{ cursor: col.sortable ? "pointer" : "default" }}
                                                    >
                                                        <div className={styles.sortIcon}>
                                                            {col.label}
                                                            {col.sortable && sortConfig.key === col.label && col.label.includes(' - ') && (
                                                                <img
                                                                    src={vector}
                                                                    alt="sort icon"
                                                                    width="14px"
                                                                    height="14px"
                                                                    className={styles.sortIconImg} 
                                                                />
                                                            )}
                                                        </div>
                                                    </StyledTableCell>
                                                    {isLastNonDateColumn && (
                                                        <StyledTableCell
                                                            colSpan={view === 'DFOS' ? 3 : 2}
                                                            sx={{ backgroundColor: "var(--primany-table-header-color)" }}
                                                        >
                                                            Total
                                                        </StyledTableCell>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableRow>
                                    <TableRow>
                                        <StyledTableCell
                                            colSpan={subHeaderColSpan()}
                                            sx={{ padding: "0", border: "1px solid #EAECF0", backgroundColor: '#FFFFFF' }}
                                        ></StyledTableCell>
                                        {Array(countSubColumns()).fill(5).map((col, idx) => (
                                            <React.Fragment key={`_${idx + 1}_`}>
                                                <StyledTableCell className={styles.subCols}>Plan</StyledTableCell>
                                                <StyledTableCell className={styles.subCols} sx={{padding: "0 5px !important"}}>{view === 'DFOS' ? "SAP Act" : "Act"}</StyledTableCell>
                                                {view === 'DFOS' && 
                                                    <StyledTableCell className={styles.subCols} sx={{padding : "0 5px !important", borderRight: idx !== 7 ? '1px solid #2b2b2b' : '1px solid #EAECF0'}}>
                                                        DFOS Act
                                                    </StyledTableCell>
                                                }
                                            </React.Fragment>
                                        ))}
                                    </TableRow>
                                </>
                            ) : (
                                <TableRow>
                                    <StyledTableCell
                                        sx={{ backgroundColor: "var(--primany-table-header-color)" }}
                                    >
                                        No columns available...
                                    </StyledTableCell>
                                </TableRow>
                            )}
                        </TableHead>
                        <TableBody>
                            {selectedColumns.length && sortedData.length > 0 ? (
                                <>
                                {displayData.map((row, idx) => {
                                    // Create stable unique key from row data
                                    const uniqueKey = `${row["Site code"] || 'na'}_${row["CBU code"] || 'na'}_${row["Cascade"] || 'na'}_${row["Line"] || 'na'}_${row["Basepack"] || 'na'}_${row["Basepack code"] || 'na'}_${idx}`;
                                    return (
                                    <StyledTableRow key={uniqueKey}>
                                        {selectedColumns.map((col, colIdx) => {
                                            // Check if this is the last non-date column
                                            const isLastNonDateColumn = !col.label.includes(' - ') && !smartProdSchedularInputFields.includes(col.label) && 
                                                colIdx === selectedColumns.findIndex((c, i) => i > colIdx && (c.label.includes(' - ') || smartProdSchedularInputFields.includes(c.label))) - 1;
                                            
                                            if (row[col.label] !== null && typeof row[col.label] === "object") {
                                                return (
                                                    <React.Fragment key={`${idx + colIdx}`}>
                                                        <StyledTableCell className={styles.planSubCol} title={(typeof row[col.label]["Plan"] == 'string' && row[col.label]["Plan"] !== 'nan') ? checkingTitle(row[col.label]["Plan"]) : ''}>
                                                            {!row[col.label]["Plan"] || row[col.label]["Plan"] === 'nan' ? 0 : row[col.label]["Plan"].toFixed(1)}
                                                        </StyledTableCell>
                                                        <StyledTableCell className={view === 'DFOS' ? styles.planSubCol : ''}>{row[col.label]["Act"].toFixed(1)}</StyledTableCell>
                                                        {view === 'DFOS' &&
                                                            <StyledTableCell>{row?.[col.label]?.["DFOS"] ? row[col.label]["DFOS"].toFixed(1) : 0}</StyledTableCell>
                                                        }
                                                    </React.Fragment>
                                                )
                                            } else {
                                                return (
                                                    <React.Fragment key={`${idx + colIdx}`}>
                                                        {col.label === 'CBU description' || col.label === "Basepack description" || col.label === "Basepack" || col.label === "CBU code" ? (
                                                            <StyledTableCell
                                                                className={`${styles.colBorder} ${styles.ellipsisText}`}>
                                                                <CustomTooltip title={row[col.label] ? row[col.label] : '-'}>
                                                                    {row[col.label] ? row[col.label] : '-'}
                                                                </CustomTooltip>
                                                            </StyledTableCell>
                                                        ) : (
                                                            <StyledTableCell
                                                                className={styles.colBorder}>
                                                                {row[col.label] || row[col.label] === 0 ? row[col.label] : '-'}
                                                            </StyledTableCell>
                                                        )}
                                                        {isLastNonDateColumn && (
                                                            <>
                                                                <StyledTableCell className={`${styles.totalCol} ${styles.planSubCol}`}>
                                                                    {rowTotal(row, "Plan")}
                                                                </StyledTableCell>
                                                                <StyledTableCell className={styles.totalCol}>
                                                                    {rowTotal(row, "Act")}
                                                                </StyledTableCell>
                                                                {view === 'DFOS' &&
                                                                    <StyledTableCell className={styles.totalCol}>
                                                                        {rowTotal(row, "DFOS")}
                                                                    </StyledTableCell>
                                                                }
                                                            </>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            }   
                                        }
                                        )}
                                    </StyledTableRow>
                                    );
                                })}
                                    <StyledTableRow className={styles.columnTotal}>
                                        <>
                                            <StyledTableCell colSpan={subHeaderColSpan() - 1} className={styles.subCols} sx={{padding:'5px !important'}}>Total</StyledTableCell>
                                            <StyledTableCell className={styles.subCols} sx={{padding:'5px !important'}}>{calculateMainColumnTotal("SAP Weekly Plan")}</StyledTableCell>
                                            <StyledTableCell className={styles.subCols} sx={{padding:'5px !important'}}>
                                                {calculateColumnTotal("Plan", 7)}
                                            </StyledTableCell>
                                            <StyledTableCell className={styles.subCols} sx={{padding:'5px !important'}}>
                                                {calculateColumnTotal("Act", 7)}
                                            </StyledTableCell>
                                            {view === 'DFOS' && 
                                                <StyledTableCell className={styles.subCols} sx={{padding:'5px !important'}}>
                                                    {calculateColumnTotal("DFOS", 7)}
                                                </StyledTableCell>
                                            }
                                            {Array(countSubColumns() - 1).fill(5).map((col, idx) => (
                                                <React.Fragment key={`_${idx + 1}_`}>
                                                    <StyledTableCell className={styles.subCols} sx={{"padding":'5px !important'}}>
                                                        {calculateColumnTotal("Plan", idx)}
                                                    </StyledTableCell>
                                                    <StyledTableCell className={styles.subCols} sx={{ borderRight: idx !== 7 && view !== 'DFOS' ? '1px solid #2b2b2b' : '1px solid #EAECF0', padding:'5px !important' }}>
                                                        {calculateColumnTotal("Act", idx)}
                                                    </StyledTableCell>
                                                    {view === 'DFOS' && 
                                                        <StyledTableCell className={styles.subCols} sx={{ borderRight: idx !== 7 && view === 'DFOS' ? '1px solid #2b2b2b' : '1px solid #EAECF0', padding:'5px !important' }}>
                                                        {calculateColumnTotal("DFOS", idx)}
                                                        </StyledTableCell>
                                                    }
                                                </React.Fragment>
                                            ))}
                                        </>
                                    </StyledTableRow>
                                </>
                            ) : (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={10} sx={{ whiteSpace: "nowrap", minWidth: "100px" }}>
                                        Data not available...
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
    )
}

export default DPTableView;